import TemplateAPI from "api/api/TemplateAPI";
import { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Card,
  CardHeader,
  Table,
  Button,
} from "reactstrap";
import PubSub from "utils/PubSub";
import PushEvents from "utils/PushEvents";
import RightArrow from "elements/RightArrow";
import EditTemplateInfoModal from "components/Modals/EditTemplateInfoModal";
import DuplicateTemplateModal from "components/Modals/DuplicateTemplateModal";
import EditModuleModal from "components/Modals/EditModuleModal";
import { AppConfirm } from "utils/PopupUtil";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import { useParams, Link, useNavigate } from "react-router-dom";

const initialModals = {
  editInfo: false,
  editTemplateModule: false,
};

const initialSubmitting = {
  templateInfo: false,
  templateModule: false,
};

const intialError = {
  updateTemplateInfo: null,
  deleteTemplate: null,
  templateModule: null,
};

const CRMTemplateDetailView = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [modules, setModules] = useState(null);
  const [modals, setModals] = useState(initialModals);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(initialSubmitting);
  const [error, setError] = useState(intialError);
  const urlStart = props.urlStart || "/supportal";

  const toggleModal = (name) => {
    setModals((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleTemplateUpdate = (template) => {
    setTemplate(template);
    setModules(template.Schema.CRM.Modules);
  };

  const getTemplate = useCallback(() => {
    TemplateAPI.getTemplateById(params.templateID)
      .then(
        (data) => {
          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
          });
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => setLoading(false));
  }, [params.templateID]);

  useEffect(() => {
    PubSub.subscribe(PushEvents.TEMPLATE.RELOADED, (data) => {
      handleTemplateUpdate(data.template);
    });
  }, []);

  useEffect(() => {
    getTemplate();
  }, [getTemplate]);

  const deleteTemplate = () => {
    TemplateAPI.deleteCRMTemplate(template.id).then(
      (data) => {
        navigate(`${urlStart}/global/templates`);
      },
      (err) => {
        let message =
          err?.response?.data?.message ||
          "An error occurred. Please try again.";

        setError((prev) => ({
          ...prev,
          deleteTemplate: message,
        }));
      }
    );
  };

  const confirmDeleteTemplate = () => {
    AppConfirm({
      title: "Confirm Deletion",
      html: "Are you sure you want to delete this template? <br/> All modules will be lost.",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteTemplate();
      }
    });
  };

  const modifyTemplateInfo = (values, setValues, initialValues) => {
    setSubmitting((prev) => ({ ...prev, templateInfo: true }));

    TemplateAPI.editCRMTemplateInfo(values.id, values.Name, values.Description)
      .then(
        (data) => {
          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
          });
          setModals((prev) => ({ ...prev, editInfo: false }));
        },
        (err) => {
          let message =
            err?.response?.data?.message ||
            "Failed to update template info. Try again.";

          setError((prev) => ({
            ...prev,
            updateTemplateInfo: message,
          }));
        }
      )
      .finally(() => {
        setSubmitting((prev) => ({ ...prev, templateInfo: false }));
      });
  };

  const duplicateTemplate = (values, setValues, initialValues) => {
    setSubmitting((prev) => ({ ...prev, duplicateTemplate: true }));

    TemplateAPI.duplicateCRMTemplate(values.id, values.Name, values.Description)
      .then(
        (data) => {
          let payload = {
            appearance: "success",
            message: (
              <>
                Successfully duplicated template.{" "}
                <a
                  href={`${urlStart}/global/templates/${data.data.data.template.id}`}
                >
                  View Template <RightArrow></RightArrow>
                </a>
              </>
            ),
          };

          PubSub.publish(PushEvents.TOAST.NEW, payload);

          setModals((prev) => ({ ...prev, duplicateTemplate: false }));
        },
        (err) => {
          let message =
            err?.response?.data?.message ||
            "Failed to duplicate template. Try again.";

          setError((prev) => ({
            ...prev,
            duplicateTemplate: message,
          }));
        }
      )
      .finally(() => {
        setSubmitting((prev) => ({ ...prev, duplicateTemplate: false }));
      });
  };

  const createTemplateModule = (values, setValues, initialValues) => {
    setSubmitting((prev) => ({ ...prev, templateModule: true }));

    TemplateAPI.createCRMTemplateModule(
      template.id,
      values.Name,
      values.APIName
    )
      .then(
        (data) => {
          setValues(initialValues);
          PubSub.publish(PushEvents.TEMPLATE.RELOADED, {
            template: data.data.data.template,
          });
          toggleModal("editTemplateModule");
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => {
        setSubmitting((prev) => ({ ...prev, templateModule: false }));
      });
  };

  return (
    <>
      <AppHelmet title="Cloud Templates - GrowthCloud.com" />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col xs="">
                <h6 className="h2 mb-0">
                  {loading ? "Loading..." : template ? template.Name : ""}
                </h6>
                <Breadcrumb
                  className="d-none d-md-block mt-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <Link to={urlStart}>
                      <i className="mdi mdi-rocket" />
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <Link to={`${urlStart}/global/templates`}>Templates</Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    {loading ? "Loading..." : template ? template.Name : ""}
                  </BreadcrumbItem>
                </Breadcrumb>
              </Col>
              <Col className="text-lg-right mt-0" xs="auto">
                <UncontrolledDropdown>
                  <DropdownToggle
                    color=""
                    className="btn-icon btn-outline-secondary"
                  >
                    <span className="btn-inner--text">Actions</span>
                    <span className="btn-inner--icon">
                      <i
                        className="mdi mdi-chevron-down"
                        style={{ position: "relative", top: "1px" }}
                      />
                    </span>
                  </DropdownToggle>
                  <DropdownMenu
                    className="dropdown-menu-arrow"
                    style={{ zIndex: 999 }}
                    right
                  >
                    <DropdownItem
                      onClick={() =>
                        setModals((prev) => ({ ...prev, editInfo: true }))
                      }
                    >
                      Edit Template Info
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        setModals((prev) => ({
                          ...prev,
                          duplicateTemplate: true,
                        }))
                      }
                    >
                      Duplicate
                    </DropdownItem>
                    <DropdownItem
                      className="text-danger"
                      onClick={(e) => {
                        e.preventDefault();
                        confirmDeleteTemplate();
                      }}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container className="mt--6" fluid>
        <hr className="mt-0 mb-4"></hr>
        <Row>
          <Col xs="12">
            <Card className="shadow">
              <CardHeader>
                <Row className="align-items-center">
                  <Col>
                    <h6 className="h3 mb-0">Template Modules</h6>
                  </Col>
                  <Col xs="auto" className="text-right">
                    <Button
                      size="sm"
                      color=""
                      className="btn-outline-secondary"
                      disabled={false}
                      onClick={() =>
                        setModals((prev) => ({
                          ...prev,
                          editTemplateModule: true,
                        }))
                      }
                    >
                      <span className="btn-inner--icon">
                        <i
                          className="mdi mdi-plus mr-2"
                          style={{ top: "0px" }}
                        />
                      </span>
                      <span className="btn-inner--text">Create Module</span>
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <Table
                className="align-items-center table-flush"
                id="globalFieldTypesTable"
                responsive
              >
                <thead className="thead-light">
                  <tr>
                    <th className="sort" scope="col">
                      Module
                    </th>
                    <th className="sort" scope="col">
                      Fields
                    </th>
                    <th className="text-right" scope="col">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="list">
                  {loading ? (
                    <tr>
                      <th scope="row" colSpan="3">
                        Loading...
                      </th>
                    </tr>
                  ) : (
                    <>
                      {modules.length ? (
                        modules.map((mod, i) => (
                          <tr key={i}>
                            <th scope="row" className="Name">
                              <div>{mod.Name}</div>
                              <small>{mod.APIName}</small>
                            </th>

                            <td className="Fields">
                              {mod.Fields.length || "No"} Fields
                            </td>

                            <td className="text-right">
                              <Button
                                color="secondary"
                                outline
                                size="sm"
                                onClick={() => {
                                  navigate({
                                    pathname: `${urlStart}/global/templates/${template.id}/${mod.APIName}/fields`,
                                    state: { template: template, module: mod },
                                  });
                                }}
                              >
                                Manage
                                <i className="ml-1 mdi mdi-chevron-right" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td>None.</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>
      <EditTemplateInfoModal
        open={modals.editInfo}
        toggle={() => toggleModal("editInfo")}
        onUpdate={modifyTemplateInfo}
        submitting={submitting.templateInfo}
        error={error.updateInfoError}
        data={template}
      />
      <DuplicateTemplateModal
        open={modals.duplicateTemplate}
        toggle={() => toggleModal("duplicateTemplate")}
        onUpdate={duplicateTemplate}
        submitting={submitting.duplicateTemplate}
        error={error.duplicateTemplate}
        data={template}
      />
      <EditModuleModal
        open={modals.editTemplateModule}
        toggle={() => toggleModal("editTemplateModule")}
        onUpdate={createTemplateModule}
        error={error.createTemplateModuleError}
        data={null}
      />
    </>
  );
};

export default CRMTemplateDetailView;
